import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row, Input, Button, Dropdown, Table } from "antd";
import type { MenuProps, TableColumnsType } from "antd";
import { useDebounce } from "@react-hook/debounce";
import { ApolloError } from "@apollo/client";
import { HiSearch } from "react-icons/hi";
import { Lock } from "lucide-react";

import { Products } from "graphql/generated/graphql";
import { useGetProductsQuery } from "graphql/queries/get-products.generated";
import { useGetProductsByOrganizationIdQuery } from "graphql/queries/get-products-by-organization-id.generated";

import ProductCard from "components/product-card/product-card";
import AdminNavPaywallGate from "components/admin-nav-paywall-gate/admin-nav-paywall-gate";

import routeNames from "constants/route-names";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useNavProfile } from "hooks/useNavProfile";
import { useAdminProductsFiltersStore } from "state/zustand-stores/admin-products-filters-store";
import { createLabelForFilters, splitDistinctTypeAndMake } from "utils/helpers";
import { SEARCH_DEBOUNCE_DELAY_MS, AdminNavPaywallSource } from "constants/constants";
import { NavProfile, UserRoleValue } from "constants/field-values";
import ViewFilter from "components/view-filter/view-filter";
import { useBrand } from "context/BrandContext";

const DEVICES_PAYWALL_ROLES = [UserRoleValue.vendor_manager, UserRoleValue.vendor_employee];

const DEVICES_PAYWALL_INCLUDED_LIST = [
	"Custom device library",
	"Manage each unique device installation and location",
	"Track serial numbers and warranties",
	"Unlimited installation photos, videos, and technical docs",
	"Continuously trained AI agent for customer-facing support"
];

const columns = [
	{
		title: "DEVICE",
		dataIndex: "name",
		key: `PRODUCT`
	},
	{
		title: "MAKE",
		dataIndex: "make",
		key: `MAKE`
	},
	{
		title: "SKU",
		dataIndex: "sku",
		key: `SKU`
	},
	{
		title: "CATEGORY",
		dataIndex: ["product_category", "label"],
		key: `PRODUCT_CATEGORY.LABEL`
	}
];

// The actual devices list. Split out from AdminProducts below so a paywalled role
// (MWA-839) never mounts its data hooks in the first place.
const AdminProductsPage: React.FC = () => {
	const { theme } = useBrand();
	const { loggedUserValue } = useLoggedUser();
	const navigate = useNavigate();

	const [dataSource, setDataSource] = useState<Array<Products>>([]);
	const [tableColumns, setTableColumns] = useState<TableColumnsType>(columns);
	const [productCategories, setProductCategories] = useState<Map<string, string>>();
	const [productMakeOptions, setProductMakeOptions] = useState<string[]>([]);
	const [categoriesDropdownOpen, setCategoriesDropdownOpen] = useState<boolean>(false);
	const [makeDropdownOpen, setMakeDropdownOpen] = useState<boolean>(false);
	const gridView = useAdminProductsFiltersStore(state => state.filters.gridView);
	const searchTerm = useAdminProductsFiltersStore(state => state.filters.searchAdminProductsTerm);
	const selectedMakesFilter = useAdminProductsFiltersStore(state => state.filters.selectedMakesFilter);
	const selectedCategoriesFilter = useAdminProductsFiltersStore(state => state.filters.selectedCategoriesFilter);

	const setGridView = useAdminProductsFiltersStore(state => state.setGridView);
	const setSearchTerm = useAdminProductsFiltersStore(state => state.setSearchTerm);
	const setMakeFilters = useAdminProductsFiltersStore(state => state.setMakeFilters);
	const setCategoryFilters = useAdminProductsFiltersStore(state => state.setCategoryFilters);

	const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY_MS);

	useEffect(() => {
		setDebouncedSearchTerm(searchTerm);
	}, [searchTerm]);

	const productsFilter = useMemo(() => {
		const whereClause: any = {
			workspace_id: { _eq: loggedUserValue.workspace_id },
			organization_id: { _eq: loggedUserValue.organization_id },
			deleted_at: { _is_null: true }
		};

		if (selectedCategoriesFilter.length) {
			whereClause.type = { _in: selectedCategoriesFilter };
		} else if (whereClause.type) {
			delete whereClause.type;
		}

		if (selectedMakesFilter.length) {
			whereClause.make = { _in: selectedMakesFilter };
		} else if (whereClause.make) {
			delete whereClause.make;
		}

		if (debouncedSearchTerm) {
			whereClause._or = [
				{ name: { _ilike: `%${debouncedSearchTerm}%` } },
				{ description: { _ilike: `%${debouncedSearchTerm}%` } },
				{ make: { _ilike: `%${debouncedSearchTerm}%` } },
				{ model: { _ilike: `%${debouncedSearchTerm}%` } },
				{ sku: { _ilike: `%${debouncedSearchTerm}%` } },
				{ product_notes: { _ilike: `%${debouncedSearchTerm}%` } },
				{
					product_attachments: {
						transcription: { _ilike: `%${debouncedSearchTerm}%` }
					}
				}
			];
		}

		return whereClause;
	}, [selectedCategoriesFilter, selectedMakesFilter, debouncedSearchTerm, loggedUserValue]);

	const handleOnClickProduct = useCallback(recordId => {
		const path = `${routeNames.ADMIN_DEVICES_DETAILS.replace(":deviceId", recordId)}`;

		navigate(path);
	}, []);

	const handleOnCompleteProducts = useCallback(data => {
		const { products } = data;
		setDataSource(products);
	}, []);

	const handleOnErrorProducts = useCallback((error: ApolloError) => {
		console.log("handleOnErrorProducts Error: ", error);
	}, []);

	const { data, loading, error } = useGetProductsQuery({
		variables: {
			where: productsFilter
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorProducts,
		onCompleted: handleOnCompleteProducts
	});

	const {
		data: productsData,
		loading: loadingProducts,
		error: productsError
	} = useGetProductsByOrganizationIdQuery({
		variables: {
			organization_id: loggedUserValue.organization_id
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true
	});

	const handleOnSelectCategoriesFilter = useCallback(
		args => {
			const { selectedKeys } = args;

			if (selectedKeys.includes("category-dropdown-option-all")) {
				setCategoryFilters([]);
				return;
			}

			setCategoryFilters(selectedKeys);
		},
		[setCategoryFilters]
	);

	const handleOnSelectMakeFilter = useCallback(
		args => {
			const { selectedKeys } = args;

			if (selectedKeys.includes("make-dropdown-option-all")) {
				setMakeFilters([]);
				return;
			}

			setMakeFilters(selectedKeys);
		},
		[setMakeFilters]
	);

	const handleSearchTermChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			// Directly update the search term in Zustand
			setSearchTerm(event.target.value);
		},
		[setSearchTerm]
	);

	useEffect(() => {
		const { distinctTypesSet, distinctMakes } = splitDistinctTypeAndMake(productsData?.products || []);
		setProductCategories(distinctTypesSet);
		setProductMakeOptions(distinctMakes);
	}, [productsData]);

	const [categoriesOptions, categoryDropdownLabel = "Category"] = useMemo(() => {
		if (productCategories?.size) {
			const selectedValues = selectedCategoriesFilter.map(v => productCategories.get(v));
			const productCategoryOptions: MenuProps["items"] = [
				{
					key: "category-dropdown-option-all",
					icon: (
						<div>
							<input
								className="custom-checkbox"
								id="category-dropdown-option-all"
								type="checkbox"
								onClick={e => e.stopPropagation()}
							/>
							<label htmlFor="category-dropdown-option-all" className="custom-checkbox-label">
								All Categories
							</label>
						</div>
					)
				}
			];

			productCategories.forEach((value, key) => {
				const optionId = `category-dropdown-option-${key}`;
				const isSelected = selectedCategoriesFilter?.includes(key);

				const newOption = {
					key: key,
					icon: (
						<div>
							<input
								className="custom-checkbox"
								id={optionId}
								type="checkbox"
								onClick={e => e.stopPropagation()}
								checked={isSelected}
							/>
							<label htmlFor={optionId} className="custom-checkbox-label">
								{value}
							</label>
						</div>
					)
				};

				productCategoryOptions.push(newOption);
			});

			return [productCategoryOptions, createLabelForFilters("Category", selectedValues)];
		}

		return [[], "Category"];
	}, [productCategories, selectedCategoriesFilter]);

	const [makeOptions, makeDropdownLabel = "Make"] = useMemo(() => {
		if (productMakeOptions && productMakeOptions.length) {
			const productMake: MenuProps["items"] = productMakeOptions.reduce(
				(options, make, currentindex, array) => {
					const optionId = `make-dropdown-option-${currentindex + 1}`;

					const isSelected = selectedMakesFilter.includes(make as string);

					const newOption = {
						key: make || "",
						icon: (
							<div>
								<input
									className="custom-checkbox"
									id={optionId}
									type="checkbox"
									onClick={e => e.stopPropagation()}
									checked={isSelected}
								/>
								<label htmlFor={optionId} className="custom-checkbox-label">
									{make}
								</label>
							</div>
						)
					};

					options.push(newOption);

					return options;
				},
				[
					{
						key: "make-dropdown-option-all",
						icon: (
							<div>
								<input
									className="custom-checkbox"
									id="make-dropdown-option-all"
									type="checkbox"
									onClick={e => e.stopPropagation()}
								/>
								<label htmlFor="make-dropdown-option-all" className="custom-checkbox-label">
									View All
								</label>
							</div>
						)
					}
				]
			);

			return [productMake, createLabelForFilters("Make", selectedMakesFilter)];
		}

		return [[], "Make"];
	}, [productMakeOptions, selectedMakesFilter]);

	return (
		<div className="admin-products-screen-container">
			<span className="page-title">{"Manage Devices"}</span>
			<div className="admin-products-content-container">
				<div className="admin-products-filter-group">
					<div className="search-products-row">
						<div className="products-search-container">
							<Input
								className="product-search-input-wrapper"
								name="searchProduct"
								placeholder="Search..."
								classNames={{ input: "product-search-input" }}
								value={searchTerm} // Use Zustand search term
								autoComplete="off"
								onChange={handleSearchTermChange}
								prefix={<HiSearch color={"#67748E"} size={"16px"} />}
								allowClear
							/>
						</div>
						<ViewFilter gridView={gridView} setGridView={setGridView} />
					</div>
					<div className="products-filters-and-create-guide-row">
						<div className="products-filters-row">
							<Dropdown
								menu={{
									items: categoriesOptions,
									selectable: true,
									multiple: true,
									onSelect: args => handleOnSelectCategoriesFilter(args),
									onDeselect: args => handleOnSelectCategoriesFilter(args),
									selectedKeys: selectedCategoriesFilter,
									defaultSelectedKeys: selectedCategoriesFilter
								}}
								arrow
								className="products-dropdown"
								trigger={["click"]}
								open={categoriesDropdownOpen}
								onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
									if (info.source === "trigger" && open === false) {
										setCategoriesDropdownOpen(false);
									}
								}}
								disabled={categoriesOptions.length === 0}
							>
								<Button
									onClick={() => {
										setCategoriesDropdownOpen(previousValue => !previousValue);
									}}
								>
									{categoryDropdownLabel}
									<img
										src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
										className="chevron-button"
										title={categoryDropdownLabel}
									/>
								</Button>
							</Dropdown>
							<Dropdown
								menu={{
									items: makeOptions,
									selectable: true,
									multiple: true,
									onSelect: args => handleOnSelectMakeFilter(args),
									onDeselect: args => handleOnSelectMakeFilter(args),
									selectedKeys: selectedMakesFilter
								}}
								arrow
								className="products-dropdown make"
								trigger={["click"]}
								open={makeDropdownOpen}
								onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
									if (info.source === "trigger" && open === false) {
										setMakeDropdownOpen(false);
									}
								}}
								disabled={makeOptions.length === 0}
							>
								<Button
									onClick={() => {
										setMakeDropdownOpen(previousValue => !previousValue);
									}}
								>
									{makeDropdownLabel}
									<img
										src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
										className="chevron-button"
										title={makeDropdownLabel}
									/>
								</Button>
							</Dropdown>
							<ViewFilter gridView={gridView} setGridView={setGridView} />
						</div>

						<div className="admin-products-cta">
							<div className="highnote-button-white-wrapper">
								<Link to={routeNames.ADMIN_DEVICE_DETECTIONS}>
									<img src={theme.buttonStarImage} alt="star" />
									Automagical Detection
								</Link>
							</div>

							<Link to={routeNames.ADMIN_DEVICES_ADD} className="highnote-button">
								Add Device
							</Link>
						</div>
					</div>
				</div>
				<div className="admin-products-table">
					{gridView ? (
						<Row className="products-grid-container" align="stretch">
							{dataSource.map(element => {
								return (
									<ProductCard
										key={`admin-product-${element.id}`}
										product={element}
										entityId={element.id}
										handleOnClickProduct={handleOnClickProduct}
									/>
								);
							})}
						</Row>
					) : (
						<div className="products-table-container">
							<Table
								dataSource={dataSource}
								columns={tableColumns}
								loading={loading}
								rowKey={record => record.id}
								onRow={(record, rowIndex) => {
									return {
										onClick: event => {
											handleOnClickProduct(record.id);
										}
									};
								}}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const AdminProducts: React.FC = () => {
	const { loggedUserValue } = useLoggedUser();
	const { navProfile } = useNavProfile();

	if (navProfile.id === NavProfile.Ollie && DEVICES_PAYWALL_ROLES.includes(loggedUserValue.role as UserRoleValue)) {
		return (
			<AdminNavPaywallGate
				source={AdminNavPaywallSource.DEVICES}
				icon={<Lock className="anpg-icon-lock" />}
				title="Unlock Device Management"
				subtitle="You shouldn't be looking through old invoices and submittals to figure out what you installed and where. Our Device Management system helps you maintain a central record for more efficient service calls and upgrades, and trains an AI agent to provide first-line support directly to your customers."
				includedList={DEVICES_PAYWALL_INCLUDED_LIST}
			/>
		);
	}

	return <AdminProductsPage />;
};

export default AdminProducts;
